/** Pure helpers for the dashboards (no React), so they can be unit tested. */

export const rs = (n) => `Rs ${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

export const compact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `${trim(v / 1e6)}M`;
  if (Math.abs(v) >= 1e3) return `${trim(v / 1e3)}K`;
  return String(Math.round(v));
};
const trim = (x) => String(Math.round(x * 10) / 10);

/** 0.125 -> "12.5%", 0.1 -> "10%". Rounds away float noise (0.07 * 100 = 7.000000000000001). */
export const ratePct = (rate) => `${Math.round((Number(rate) || 0) * 1000) / 10}%`;

export const percent = (ratio, digits = 0) =>
  ratio === null || ratio === undefined ? '–' : `${(ratio * 100).toFixed(digits)}%`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function shortDate(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function longDate(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "29 Oct, 11:42" for the activity feed. */
export function dateTime(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${hh}:${mm}`;
}

/** Status bucket from the API -> pill colours (same palette as the design). */
export const BUCKET_TONE = {
  Approved: { bg: '#dcf5e8', fg: '#0a6038', bar: '#1f7a5c' },
  Pending: { bg: '#fff1cf', fg: '#7a4d00', bar: '#d98a00' },
  'In progress': { bg: '#e3edfc', fg: '#1d4ea8', bar: '#2f6fd1' },
  Declined: { bg: '#fde8e5', fg: '#9b2418', bar: '#c8372a' },
};
export const RESELLER_TONE = {
  active: { bg: '#dcf5e8', fg: '#0a6038' },
  pending: { bg: '#fff1cf', fg: '#7a4d00' },
  suspended: { bg: '#e8edf5', fg: '#3b4a5e' },
};

/** Round the chart ceiling up to 1, 2, 2.5, 5 or 10 times a power of ten. */
export function niceMax(value) {
  const v = Number(value) || 0;
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / exp;
  const step = [1, 2, 2.5, 5, 10].find((s) => f <= s);
  return step * exp;
}

export const barHeight = (value, max, px) => (max > 0 ? Math.round((Math.max(0, value) / max) * px) : 0);

/** One CSV field. Quotes when needed and defuses spreadsheet formulas (=, +, -, @). */
export function csvCell(v) {
  const s = v === null || v === undefined ? '' : String(v);
  // Only text can carry a formula; real numbers (even negative ones) pass through.
  const safe = typeof v === 'string' && /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv(columns, rows) {
  const head = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(typeof c.value === 'function' ? c.value(r) : r[c.value])).join(','));
  return [head, ...body].join('\r\n');
}

export function downloadCsv(filename, text) {
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Message from an axios error (FastAPI detail may be a string or a validation list). */
export function apiError(err, fallback = 'Something went wrong') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) return d.map((x) => x.msg).join(', ');
  return fallback;
}

/**
 * Page numbers to show: always the first and last, and a window around the current page, with
 * 'gap' where numbers are skipped. pageWindow(5, 10) -> [1, 'gap', 4, 5, 6, 'gap', 10].
 */
export function pageWindow(page, pages, around = 1) {
  if (pages <= 1) return [1];
  const keep = new Set([1, pages]);
  for (let p = page - around; p <= page + around; p += 1) if (p >= 1 && p <= pages) keep.add(p);
  const sorted = [...keep].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] === 2) out.push(sorted[i - 1] + 1); // a single missing number is shown, not hidden
    else if (i > 0 && p - sorted[i - 1] > 2) out.push('gap');
    out.push(p);
  });
  return out;
}
